import React, { useState, useEffect, useRef } from "react";
import { User, LogOut, Mail, MapPin, Phone, ChevronDown } from "lucide-react";

export default function UserProfile({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // ✅ MOVE useEffect BEFORE the early return
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // ✅ NOW the early return is after all hooks
  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group relative flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2 pr-3 transition-all hover:border-white/20 hover:bg-white/[0.06] active:scale-95"
      >
        {/* Avatar */}
        <div className="relative">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#C5A059] to-[#D4AF37] font-bold text-[#0A0709] shadow-lg">
            {user.name.charAt(0).toUpperCase()}
          </div>
          {/* Online indicator */}
          <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0A0709] bg-green-500" />
        </div>

        {/* User name (desktop only) */}
        <div className="hidden items-center gap-1 md:flex">
          <span className="text-sm font-medium text-white">
            {user.name.split(" ")[0]}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-white/60 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-14 z-50 w-72 origin-top-right animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0A0709]/95 shadow-2xl ring-1 ring-black/5 backdrop-blur-2xl">
            {/* Header with gradient */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#C5A059]/10 via-transparent to-transparent p-5 pb-4">
              {/* Decorative blur */}
              <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#C5A059]/15 blur-3xl" />

              <div className="relative flex items-start gap-3">
                {/* Large Avatar */}
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] text-xl font-bold text-[#0A0709] shadow-lg">
                  {user.name.charAt(0).toUpperCase()}
                </div>

                {/* User Info */}
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-base font-semibold text-white">
                    {user.name}
                  </h3>
                  <p className="mt-0.5 text-xs text-white/60">
                    {user.role === "driver" ? "Driver Account" : "Customer Account"}
                  </p>
                </div>
              </div>
            </div>

            {/* User Details */}
            <div className="space-y-1 border-t border-white/10 p-3">
              {/* Email */}
              {user.email && (
                <div className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white/[0.03]">
                  <Mail className="h-4 w-4 shrink-0 text-white/40" />
                  <span className="truncate text-sm text-white/70">
                    {user.email}
                  </span>
                </div>
              )}

              {/* Phone */}
              <div className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white/[0.03]">
                <Phone className="h-4 w-4 shrink-0 text-white/40" />
                <span className="text-sm text-white/70">+91 {user.phone}</span>
              </div>

              {/* City */}
              <div className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white/[0.03]">
                <MapPin className="h-4 w-4 shrink-0 text-white/40" />
                <span className="text-sm text-white/70">{user.area || user.city || "Mumbai"}</span>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-white/10 p-3">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onLogout();
                }}
                className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-400 transition-all hover:bg-red-500/10 active:scale-95"
              >
                <LogOut className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}